import { Check, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";

import type {
  CashierCryptoAsset,
  CashierFlow,
  CashierWalletView,
} from "../types";
import { groupCryptoAssets } from "./registry";

const cryptoAssetKey = (asset: CashierCryptoAsset) =>
  `${asset.symbol.trim().toUpperCase()}:${asset.network}`;

export function CryptoAssetPicker({
  assets,
  flow,
  onSelect,
  selected,
  balances = [],
  showBalances = false,
}: {
  assets: readonly CashierCryptoAsset[];
  flow: CashierFlow;
  onSelect: (asset: CashierCryptoAsset) => void;
  selected: CashierCryptoAsset | null;
  balances?: readonly CashierWalletView[];
  showBalances?: boolean;
}) {
  const groups = useMemo(() => groupCryptoAssets(assets, flow), [assets, flow]);
  const [expandedSymbol, setExpandedSymbol] = useState<string | null>(
    selected?.symbol.trim().toUpperCase() ?? null,
  );
  const selectedSymbol = selected?.symbol.trim().toUpperCase() ?? null;
  const effectiveExpandedSymbol =
    selectedSymbol && groups.some((group) => group.symbol === selectedSymbol)
      ? selectedSymbol
      : groups.some((group) => group.symbol === expandedSymbol)
        ? expandedSymbol
        : null;

  if (!groups.length) {
    return (
      <p className="cashier-crypto-picker__empty" role="status">
        No crypto assets are available.
      </p>
    );
  }

  const expanded =
    groups.find((group) => group.symbol === effectiveExpandedSymbol) ?? null;

  return (
    <div className="cashier-crypto-picker">
      <div
        aria-label={`${flow === "deposit" ? "Deposit" : "Withdrawal"} crypto assets`}
        className="cashier-crypto-picker__assets"
        role="radiogroup"
      >
        {groups.map((group) => {
          const groupSelected =
            selected?.symbol.trim().toUpperCase() === group.symbol;
          const balance =
            balances.find(
              (wallet) => wallet.currency.toUpperCase() === group.symbol,
            ) ?? null;
          const disabled = showBalances && !balance;
          return (
            <button
              aria-expanded={
                group.networks.length > 1
                  ? effectiveExpandedSymbol === group.symbol
                  : undefined
              }
              aria-checked={groupSelected}
              aria-disabled={disabled || undefined}
              className="cashier-crypto-picker__asset"
              data-empty={disabled || undefined}
              data-selected={groupSelected || undefined}
              disabled={disabled}
              key={group.symbol}
              onClick={() => {
                setExpandedSymbol(group.symbol);
                if (group.networks.length === 1 && group.networks[0]) {
                  onSelect(group.networks[0]);
                }
              }}
              role="radio"
              type="button"
            >
              <img alt="" aria-hidden src={group.iconUrl} />
              <span>
                <strong>{group.symbol}</strong>
                <small>
                  {showBalances
                    ? balance
                      ? `${balance.total} ${group.symbol}`
                      : `0 ${group.symbol}`
                    : group.networks.length > 1
                      ? `${group.networks.length} networks`
                      : ""}
                </small>
              </span>
              {groupSelected && group.networks.length === 1 ? (
                <Check aria-hidden />
              ) : (
                <ChevronRight aria-hidden />
              )}
            </button>
          );
        })}
      </div>

      {expanded && expanded.networks.length > 1 ? (
        <fieldset className="cashier-network-picker">
          <legend>Select network for {expanded.symbol}</legend>
          <div className="cashier-network-picker__options">
            {expanded.networks.map((asset) => {
              const checked =
                selected != null &&
                cryptoAssetKey(selected) === cryptoAssetKey(asset);
              return (
                <label
                  className="cashier-network-picker__option"
                  data-selected={checked || undefined}
                  key={cryptoAssetKey(asset)}
                >
                  <input
                    checked={checked}
                    name={`cashier-${flow}-${expanded.symbol}-network`}
                    onChange={() => onSelect(asset)}
                    type="radio"
                    value={asset.network}
                  />
                  <span>
                    <strong>{asset.networkName || asset.network}</strong>
                    <small>{asset.network}</small>
                  </span>
                  {checked ? <Check aria-hidden /> : null}
                </label>
              );
            })}
          </div>
        </fieldset>
      ) : null}
    </div>
  );
}
